import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import BackButton from "@/components/BackButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import {
  Shield, Trash2, Loader2, FileClock, Plus, Building, UserPlus, KeyRound, Check, X,
} from "lucide-react";

import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  loadOrganizations, createOrganization, updateUserOrganization,
  loadOrganizationExportSettings, updateOrganizationExportSettings,
  DEFAULT_ORG_EXPORT_SETTINGS,
  type Organization, type OrgExportSettings,
} from "@/lib/organizations";

interface UserProfile {
  id: string;
  full_name: string;
  email: string;
  isAdmin: boolean;
  canAccessIssuedInvoices: boolean;
  canAccessFinancing: boolean;
  canAccessPayments: boolean;
  companyIds: string[];
  organization_id: string;
}

interface Company {
  id: string;
  name: string;
  tax_id: string | null;
  a3_company_code: string | null;
  organization_id: string;
}

interface PasswordResetRow {
  id: string;
  email: string;
  requested_at: string;
}

const generateTemporaryPassword = () => {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnopqrstuvwxyz";
  const numbers = "23456789";
  const symbols = "!@#%*-_?";
  const all = `${upper}${lower}${numbers}${symbols}`;
  const pick = (chars: string) => chars[Math.floor(crypto.getRandomValues(new Uint32Array(1))[0] / (0xffffffff + 1) * chars.length)];
  const chars = [pick(upper), pick(lower), pick(numbers), pick(symbols), ...Array.from({ length: 10 }, () => pick(all))];
  return chars.sort(() => crypto.getRandomValues(new Uint32Array(1))[0] - 0x7fffffff).join("");
};

const getFunctionErrorMessage = async (error: unknown, fallback: string) => {
  const maybeError = error as { context?: Response; message?: string };
  const response = maybeError.context;
  if (response && typeof response.clone === "function") {
    try {
      const payload = await response.clone().json();
      if (payload?.error) return String(payload.error);
    } catch {
      try {
        const text = await response.clone().text();
        if (text) return text;
      } catch {
        // Mantener el mensaje original de la librería si no se puede leer el cuerpo.
      }
    }
  }
  return maybeError.message || fallback;
};

const AdminPanel = () => {
  const { isAdmin, loading: authLoading, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [orgSettings, setOrgSettings] = useState<OrgExportSettings>(DEFAULT_ORG_EXPORT_SETTINGS);
  const [savingOrgSettings, setSavingOrgSettings] = useState(false);
  const [activeOrgId, setActiveOrgId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [userToDelete, setUserToDelete] = useState<UserProfile | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [newOrgOpen, setNewOrgOpen] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: "", slug: "" });
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", password: "", fullName: "", makeAdmin: false });
  const [creatingUser, setCreatingUser] = useState(false);

  const [resetRequests, setResetRequests] = useState<Array<{ id: string; email: string; requested_at: string; userExists: boolean }>>([]);
  const [approveTarget, setApproveTarget] = useState<{ id: string; email: string } | null>(null);
  const [approveTempPassword, setApproveTempPassword] = useState("");
  const [approving, setApproving] = useState(false);



  

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate("/app");
    }
  }, [authLoading, isAdmin, navigate]);

  const loadData = async () => {
    setLoading(true);

    const [{ data: profiles }, { data: roles }, { data: access }, { data: comps }, orgs, { data: resets }] = await Promise.all([
      supabase.from("profiles").select("id, full_name, email, organization_id, can_access_issued_invoices, can_access_financing, can_access_payments"),
      supabase.from("user_roles").select("user_id, role"),
      supabase.from("user_company_access").select("user_id, company_id"),
      supabase.from("company_masters").select("id, name, tax_id, a3_company_code, organization_id").order("name"),
      loadOrganizations(),
      supabase.from("password_reset_requests" as any).select("id, email, requested_at").eq("status", "pending").order("requested_at", { ascending: false }),
    ]);


    const roleMap = new Map<string, boolean>();
    (roles || []).forEach((r: any) => {
      if (r.role === "admin") roleMap.set(r.user_id, true);
    });

    const accessMap = new Map<string, string[]>();
    (access || []).forEach((a: any) => {
      const list = accessMap.get(a.user_id) || [];
      list.push(a.company_id);
      accessMap.set(a.user_id, list);
    });

    const profileRows = profiles || [];

    setUsers(
      profileRows.map((p) => ({
        id: p.id,
        full_name: p.full_name || "",
        email: p.email || "",
        isAdmin: roleMap.get(p.id) || false,
        canAccessIssuedInvoices: !!p.can_access_issued_invoices,
        canAccessFinancing: !!(p as any).can_access_financing,
        canAccessPayments: !!(p as any).can_access_payments,
        companyIds: accessMap.get(p.id) || [],
        organization_id: p.organization_id,
      }))
    );
    setCompanies((comps || []).map((c: any) => ({
      id: c.id,
      name: c.name,
      tax_id: c.tax_id ?? null,
      a3_company_code: c.a3_company_code ?? null,
      organization_id: c.organization_id,
    })));
    setOrganizations(orgs);
    const knownProfileEmails = new Set(
      profileRows
        .map((p) => String(p.email || "").trim().toLowerCase())
        .filter(Boolean)
    );
    setResetRequests(
      (((resets as unknown as PasswordResetRow[]) || [])).map((request) => ({
        ...request,
        userExists: knownProfileEmails.has(String(request.email || "").trim().toLowerCase()),
      }))
    );
    if (!activeOrgId && orgs.length > 0) {
      setActiveOrgId(orgs[0].id);
    }
    setLoading(false);

  };

  useEffect(() => {
    if (isAdmin) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const filteredCompanies = useMemo(
    () => companies.filter((c) => c.organization_id === activeOrgId),
    [companies, activeOrgId]
  );
  const filteredUsers = useMemo(
    () => users.filter((u) => u.organization_id === activeOrgId),
    [users, activeOrgId]
  );

  const activeOrgName = useMemo(
    () => organizations.find((o) => o.id === activeOrgId)?.name ?? "",
    [organizations, activeOrgId]
  );

  useEffect(() => {
    if (!activeOrgId) {
      setOrgSettings(DEFAULT_ORG_EXPORT_SETTINGS);
      return;
    }
    let cancelled = false;
    loadOrganizationExportSettings(activeOrgId).then((s) => {
      if (!cancelled) setOrgSettings(s);
    });
    return () => { cancelled = true; };
  }, [activeOrgId]);

  const saveOrgSettings = async (patch: Partial<OrgExportSettings>) => {
    if (!activeOrgId) return;
    setOrgSettings({ ...orgSettings, ...patch });
    setSavingOrgSettings(true);
    const { error } = await updateOrganizationExportSettings(activeOrgId, patch);
    setSavingOrgSettings(false);
    if (error) {
      setOrgSettings(orgSettings);
      toast({ title: "No se pudo guardar el ajuste", description: error, variant: "destructive" });
    } else {
      toast({ title: "Ajuste guardado" });
    }
  };


  const toggleAdmin = async (userId: string, currentlyAdmin: boolean) => {
    if (currentlyAdmin) {
      await supabase.from("user_roles").delete().eq("user_id", userId).eq("role", "admin");
    } else {
      await supabase.from("user_roles").insert({ user_id: userId, role: "admin" } as any);
    }
    loadData();
  };

  const toggleIssuedAccess = async (userId: string, currentValue: boolean) => {
    const { error } = await supabase
      .from("profiles")
      .update({ can_access_issued_invoices: !currentValue } as any)
      .eq("id", userId);
    if (error) {
      toast({ title: "Error al actualizar acceso", description: error.message, variant: "destructive" });
      return;
    }
    loadData();
  };

  const togglePaymentsAccess = async (userId: string, currentValue: boolean) => {
    const { error } = await supabase
      .from("profiles")
      .update({ can_access_payments: !currentValue } as any)
      .eq("id", userId);
    if (error) {
      toast({ title: "Error al actualizar acceso", description: error.message, variant: "destructive" });
      return;
    }
    loadData();
  };

  const openPaymentsToOrg = async (enable: boolean) => {
    if (!activeOrgId) return;
    const { error } = await supabase
      .from("profiles")
      .update({ can_access_payments: enable } as any)
      .eq("organization_id", activeOrgId);
    if (error) {
      toast({ title: "Error al actualizar acceso", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: enable ? "Agenda de pagos habilitada para la asesoría" : "Agenda de pagos retirada de la asesoría" });
    loadData();
  };

  const toggleFinancingAccess = async (userId: string, currentValue: boolean) => {
    const { error } = await supabase
      .from("profiles")
      .update({ can_access_financing: !currentValue } as any)
      .eq("id", userId);
    if (error) {
      toast({ title: "Error al actualizar acceso", description: error.message, variant: "destructive" });
      return;
    }
    loadData();
  };

  const toggleCompanyAccess = async (userId: string, companyId: string, hasAccess: boolean) => {
    if (hasAccess) {
      await supabase
        .from("user_company_access")
        .delete()
        .eq("user_id", userId)
        .eq("company_id", companyId);
    } else {
      await supabase
        .from("user_company_access")
        .insert({ user_id: userId, company_id: companyId } as any);
    }
    loadData();
  };

  const moveUserToOrg = async (userId: string, orgId: string) => {
    // Al cambiar de asesoría, se purgan accesos a empresas de la anterior
    const user = users.find((u) => u.id === userId);
    if (user) {
      const oldCompanies = companies
        .filter((c) => c.organization_id === user.organization_id)
        .map((c) => c.id);
      if (oldCompanies.length > 0) {
        await supabase
          .from("user_company_access")
          .delete()
          .eq("user_id", userId)
          .in("company_id", oldCompanies);
      }
    }
    const { error } = await updateUserOrganization(userId, orgId);
    if (error) {
      toast({ title: "Error", description: error, variant: "destructive" });
    } else {
      toast({ title: "Usuario movido de asesoría" });
      loadData();
    }
  };

  const handleDeleteCompany = async () => {
    if (!companyToDelete) return;
    setDeleting(true);
    try {
      const { data: contracts } = await supabase
        .from("leasing_contracts")
        .select("id")
        .eq("company_id", companyToDelete.id);
      const contractIds = (contracts || []).map((c: any) => c.id);
      if (contractIds.length > 0) {
        await supabase.from("leasing_schedule").delete().in("contract_id", contractIds);
      }
      await supabase.from("leasing_contracts").delete().eq("company_id", companyToDelete.id);
      await supabase.from("supplier_master").delete().eq("company_id", companyToDelete.id);
      await supabase.from("user_company_access").delete().eq("company_id", companyToDelete.id);

      const { error } = await supabase
        .from("company_masters")
        .delete()
        .eq("id", companyToDelete.id);

      if (error) throw error;
      toast({ title: "Empresa eliminada", description: `"${companyToDelete.name}" se ha borrado correctamente.` });
      setCompanyToDelete(null);
      loadData();
    } catch (err: any) {
      toast({ title: "Error al eliminar", description: err.message, variant: "destructive" });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setDeletingUser(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-user", {
        body: { userId: userToDelete.id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast({ title: "Usuario eliminado", description: userToDelete.email });
      setUserToDelete(null);
      loadData();
    } catch (err: any) {
      toast({ title: "Error al eliminar usuario", description: err.message, variant: "destructive" });
    } finally {
      setDeletingUser(false);
    }
  };



  const handleCreateOrg = async () => {
    if (!newOrg.name.trim() || !newOrg.slug.trim()) {
      toast({ title: "Nombre y código son obligatorios", variant: "destructive" });
      return;
    }
    setCreatingOrg(true);
    const { data, error } = await createOrganization(newOrg.name, newOrg.slug);
    setCreatingOrg(false);
    if (error || !data) {
      toast({ title: "Error al crear asesoría", description: error, variant: "destructive" });
      return;
    }
    toast({ title: "Asesoría creada", description: data.name });
    setNewOrg({ name: "", slug: "" });
    setNewOrgOpen(false);
    setActiveOrgId(data.id);
    loadData();
  };

  const handleCreateUser = async () => {
    const email = newUser.email.trim().toLowerCase();
    const tempPassword = newUser.password.trim();
    if (!email || tempPassword.length < 12) {
      toast({ title: "Email y contraseña (mín. 12) son obligatorios", variant: "destructive" });
      return;
    }
    if (!activeOrgId) {
      toast({ title: "Selecciona una asesoría activa antes de crear el usuario", variant: "destructive" });
      return;
    }
    setCreatingUser(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-user", {
        body: {
          email,
          password: tempPassword,
          fullName: newUser.fullName.trim(),
          organizationId: activeOrgId,
          makeAdmin: newUser.makeAdmin,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast({ title: "Usuario creado", description: `${email} ya puede iniciar sesión.` });
      setNewUser({ email: "", password: "", fullName: "", makeAdmin: false });
      setNewUserOpen(false);
      loadData();
    } catch (err: any) {
      toast({ title: "Error al crear usuario", description: err.message, variant: "destructive" });
    } finally {
      setCreatingUser(false);
    }
  };

  const handleApproveReset = async () => {
    if (!approveTarget) return;
    const tempPassword = approveTempPassword.trim();
    if (tempPassword.length < 12) {
      toast({ title: "La contraseña provisional debe tener al menos 12 caracteres", variant: "destructive" });
      return;
    }
    setApproving(true);
    try {
      const { data, error } = await supabase.functions.invoke("approve-password-reset", {
        body: { requestId: approveTarget.id, tempPassword },
      });
      if (error) {
        const description = await getFunctionErrorMessage(error, "No se pudo aprobar el reseteo.");
        toast({ title: "Error al aprobar", description, variant: "destructive" });
        if (/No existe ningún usuario|Solicitud no encontrada|ya ha sido resuelta/i.test(description)) {
          setApproveTarget(null);
          setApproveTempPassword("");
          await loadData();
        }
        return;
      }
      if (data?.error || data?.ok === false) {
        const description = String(data?.error || "No se pudo aprobar el reseteo.");
        toast({ title: "Error al aprobar", description, variant: "destructive" });
        if (data?.code === "USER_NOT_FOUND" || /No existe ningún usuario/i.test(description)) {
          setApproveTarget(null);
          setApproveTempPassword("");
          await loadData();
        }
        return;
      }
      toast({
        title: "Reseteo aprobado",
        description: `Comparte con ${approveTarget.email} la contraseña provisional «${tempPassword}». Se le pedirá que la cambie al entrar.`,
      });
      setApproveTarget(null);
      setApproveTempPassword("");
      loadData();
    } catch (err: any) {
      toast({ title: "Error al aprobar", description: err.message, variant: "destructive" });
    } finally {
      setApproving(false);
    }
  };

  const handleRejectReset = async (id: string) => {
    const { error } = await supabase
      .from("password_reset_requests" as any)
      .update({ status: "rejected", resolved_at: new Date().toISOString(), resolved_by: currentUser?.id } as any)
      .eq("id", id);
    if (error) {
      toast({ title: "Error al rechazar", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Solicitud rechazada" });
    loadData();
  };



  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-background p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <BackButton variant="ghost" />
        <Shield className="h-6 w-6 text-[hsl(var(--brand-green))]" />
        <h1 className="text-xl font-semibold text-foreground">Panel de Administración</h1>
        <div className="ml-auto">
          <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate("/auditoria")}>
            <FileClock className="h-4 w-4" /> Registro de auditoría
          </Button>
        </div>
      </div>

      {/* Selector de asesoría */}
      <div className="flex items-center gap-3 mb-6 p-4 rounded-lg border bg-card">
        <Building className="h-5 w-5 text-muted-foreground shrink-0" />
        <Label className="text-sm font-medium shrink-0">Asesoría activa:</Label>
        <Select value={activeOrgId} onValueChange={setActiveOrgId}>
          <SelectTrigger className="w-[280px]">
            <SelectValue placeholder="Selecciona asesoría" />
          </SelectTrigger>
          <SelectContent>
            {organizations.map((o) => (
              <SelectItem key={o.id} value={o.id}>
                {o.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">
          {filteredCompanies.length} empresa{filteredCompanies.length !== 1 ? "s" : ""} · {filteredUsers.length} usuario{filteredUsers.length !== 1 ? "s" : ""}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" className="gap-2" onClick={() => setNewUserOpen(true)}>
            <UserPlus className="h-4 w-4" /> Nuevo usuario
          </Button>
          <Button size="sm" variant="outline" className="gap-2" onClick={() => setNewOrgOpen(true)}>
            <Plus className="h-4 w-4" /> Nueva asesoría
          </Button>
        </div>
      </div>

      {/* Ajustes de traspaso a A3 de la asesoría */}
      {activeOrgId && (
        <div className="mb-6 p-4 rounded-lg border bg-card">
          <h2 className="text-sm font-semibold mb-1">Criterios de traspaso a A3 (facturas recibidas)</h2>
          <p className="text-xs text-muted-foreground mb-3">
            Se aplican a todos los usuarios de esta asesoría, tanto en el procesador desde PDF como en el conversor desde Excel.
          </p>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={orgSettings.useInvoiceDateAsEntryDate}
                disabled={savingOrgSettings}
                onCheckedChange={(v) => saveOrgSettings({ useInvoiceDateAsEntryDate: !!v })}
              />
              Usar la fecha de la factura como fecha de contabilización
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={orgSettings.omitVatLedgerNumber}
                disabled={savingOrgSettings}
                onCheckedChange={(v) => saveOrgSettings({ omitVatLedgerNumber: !!v })}
              />
              No traspasar el número de registro de IVA (se usa el número de factura del proveedor)
            </label>
          </div>
        </div>
      )}

      {activeOrgId && (
        <div className="mb-6 p-4 rounded-lg border bg-card flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[240px]">
            <h2 className="text-sm font-semibold mb-1">Agenda de pagos</h2>
            <p className="text-xs text-muted-foreground">
              Concede o retira el módulo a todos los usuarios de {activeOrgName}. También puedes hacerlo usuario a usuario en la columna «Acceso Pagos».
            </p>
          </div>
          <Button size="sm" onClick={() => openPaymentsToOrg(true)}>Habilitar a toda la asesoría</Button>
          <Button size="sm" variant="outline" onClick={() => openPaymentsToOrg(false)}>Retirar a toda la asesoría</Button>
        </div>
      )}


      {/* Solicitudes de reseteo de contraseña pendientes */}
      {resetRequests.length > 0 && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30 p-4">
          <div className="flex items-center gap-2 mb-3">
            <KeyRound className="h-5 w-5 text-amber-700 dark:text-amber-400" />
            <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
              Solicitudes de reseteo de contraseña ({resetRequests.length})
            </h2>
          </div>
          <div className="space-y-2">
            {resetRequests.map((r) => (
              <div key={r.id} className="flex items-center gap-3 bg-background rounded-md border p-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{r.email}</p>
                  <p className="text-xs text-muted-foreground">
                    Solicitado el {new Date(r.requested_at).toLocaleString("es-ES")}
                  </p>
                  {!r.userExists && (
                    <p className="text-xs text-destructive">
                      El email no coincide con ningún usuario. Rechaza la solicitud y pide que la hagan con el email correcto.
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  className="gap-1.5 bg-[hsl(var(--brand-green))] hover:bg-[hsl(var(--brand-green))]/90 text-white"
                  disabled={!r.userExists}
                  title={!r.userExists ? "No se puede aprobar porque el email no existe" : undefined}
                  onClick={() => { setApproveTarget({ id: r.id, email: r.email }); setApproveTempPassword(""); }}
                >
                  <Check className="h-4 w-4" /> Aprobar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={() => handleRejectReset(r.id)}
                >
                  <X className="h-4 w-4" /> Rechazar
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}



      <div className="rounded-lg border bg-card overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[180px]">Usuario ({activeOrgName})</TableHead>
              <TableHead className="min-w-[200px]">Email</TableHead>
              <TableHead className="min-w-[180px]">Asesoría</TableHead>
              <TableHead className="w-[80px] text-center">Admin</TableHead>
              <TableHead className="w-[120px] text-center text-xs">Acceso Emitidas</TableHead>
              <TableHead className="w-[120px] text-center text-xs">Acceso Financiación</TableHead>
              <TableHead className="w-[120px] text-center text-xs">Acceso Pagos</TableHead>
              <TableHead className="w-[60px] text-center">Borrar</TableHead>
              {filteredCompanies.map((c) => (
                <TableHead key={c.id} className="text-center min-w-[140px] text-xs">
                  <div className="flex flex-col items-center gap-1">
                    <span className="font-medium leading-tight">{c.name}</span>
                    <span className="text-[10px] text-muted-foreground font-normal">
                      {c.a3_company_code ? `A3 ${c.a3_company_code}` : "—"}
                      {c.tax_id ? ` · ${c.tax_id}` : ""}
                    </span>
                    <button
                      type="button"
                      onClick={() => setCompanyToDelete(c)}
                      className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                      title={`Eliminar empresa "${c.name}"`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="font-medium">{u.full_name || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                <TableCell>
                  <Select
                    value={u.organization_id}
                    onValueChange={(v) => moveUserToOrg(u.id, v)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {organizations.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          {o.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-center">
                  <Checkbox
                    checked={u.isAdmin}
                    onCheckedChange={() => toggleAdmin(u.id, u.isAdmin)}
                  />
                </TableCell>
                <TableCell className="text-center">
                  <Checkbox
                    checked={u.isAdmin || u.canAccessIssuedInvoices}
                    disabled={u.isAdmin}
                    onCheckedChange={() => toggleIssuedAccess(u.id, u.canAccessIssuedInvoices)}
                    title={u.isAdmin ? "Los admin tienen acceso automáticamente" : "Permite/oculta el módulo Facturas emitidas"}
                  />
                </TableCell>
                <TableCell className="text-center">
                  <Checkbox
                    checked={u.isAdmin || u.canAccessFinancing}
                    disabled={u.isAdmin}
                    onCheckedChange={() => toggleFinancingAccess(u.id, u.canAccessFinancing)}
                    title={u.isAdmin ? "Los admin tienen acceso automáticamente" : "Permite/oculta el módulo Financiación"}
                  />
                </TableCell>
                <TableCell className="text-center">
                  <Checkbox
                    checked={u.canAccessPayments}
                    onCheckedChange={() => togglePaymentsAccess(u.id, u.canAccessPayments)}
                    title="Permite/oculta la Agenda de pagos"
                  />
                </TableCell>
                <TableCell className="text-center">
                  <button
                    type="button"
                    onClick={() => setUserToDelete(u)}
                    disabled={u.id === currentUser?.id}
                    title={u.id === currentUser?.id ? "No puedes eliminarte a ti mismo" : `Eliminar usuario ${u.email}`}
                    className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </TableCell>
                {filteredCompanies.map((c) => {
                  const hasAccess = u.companyIds.includes(c.id);
                  return (
                    <TableCell key={c.id} className="text-center">
                      <Checkbox
                        checked={hasAccess}
                        onCheckedChange={() => toggleCompanyAccess(u.id, c.id, hasAccess)}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {filteredUsers.length === 0 && (
        <p className="text-center text-muted-foreground py-8 text-sm">
          No hay usuarios en esta asesoría.
        </p>
      )}

      <AlertDialog open={!!companyToDelete} onOpenChange={(open) => !open && setCompanyToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar empresa "{companyToDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminarán también todos los proveedores del maestro,
              contratos de leasing, cuadros de cuotas y accesos de usuarios asociados a esta empresa.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDeleteCompany(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Eliminar definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!userToDelete} onOpenChange={(open) => !open && setUserToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar usuario "{userToDelete?.email}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará la cuenta de acceso y todos sus
              accesos a empresas, correcciones de proveedores, historial de cuentas y sesiones de trabajo.
              Las facturas ya contabilizadas y el histórico de la empresa no se ven afectados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingUser}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDeleteUser(); }}
              disabled={deletingUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingUser ? <Loader2 className="h-4 w-4 animate-spin" /> : "Eliminar usuario"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      <Dialog open={newOrgOpen} onOpenChange={setNewOrgOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva asesoría</DialogTitle>
            <DialogDescription>
              Las empresas y usuarios de esta asesoría se mantendrán completamente separados de las demás.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="org-name">Nombre</Label>
              <Input
                id="org-name"
                value={newOrg.name}
                onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                placeholder="Asesoría García"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-slug">Código corto (sin espacios, solo minúsculas y guiones)</Label>
              <Input
                id="org-slug"
                value={newOrg.slug}
                onChange={(e) => setNewOrg({ ...newOrg, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
                placeholder="garcia"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOrgOpen(false)} disabled={creatingOrg}>
              Cancelar
            </Button>
            <Button onClick={handleCreateOrg} disabled={creatingOrg}>
              {creatingOrg ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear asesoría"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={newUserOpen} onOpenChange={setNewUserOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo usuario</DialogTitle>
            <DialogDescription>
              Se creará la cuenta directamente, sin necesidad de verificación por email. Se asignará a la asesoría activa
              ({activeOrgName || "ninguna"}). Después podrás marcar a qué empresas tiene acceso.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="new-user-name">Nombre completo</Label>
              <Input
                id="new-user-name"
                value={newUser.fullName}
                onChange={(e) => setNewUser({ ...newUser, fullName: e.target.value })}
                placeholder="Juan Pérez"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-user-email">Email</Label>
              <Input
                id="new-user-email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="juan@empresa.es"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-user-password">Contraseña provisional (mín. 12 caracteres)</Label>
              <Input
                id="new-user-password"
                type="text"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="••••••••"
                autoComplete="new-password"
              />
              <Button type="button" variant="outline" size="sm" onClick={() => setNewUser({ ...newUser, password: generateTemporaryPassword() })}>
                Generar contraseña provisional
              </Button>
              <p className="text-xs text-muted-foreground">
                Compártela por un canal seguro. Al entrar se le pedirá cambiarla por una contraseña definitiva.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="new-user-admin"
                checked={newUser.makeAdmin}
                onCheckedChange={(v) => setNewUser({ ...newUser, makeAdmin: !!v })}
              />
              <Label htmlFor="new-user-admin" className="text-sm font-normal cursor-pointer">
                Conceder permisos de administrador
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewUserOpen(false)} disabled={creatingUser}>
              Cancelar
            </Button>
            <Button onClick={handleCreateUser} disabled={creatingUser}>
              {creatingUser ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear usuario"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!approveTarget} onOpenChange={(open) => { if (!open) { setApproveTarget(null); setApproveTempPassword(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aprobar reseteo de contraseña</DialogTitle>
            <DialogDescription>
              Define una contraseña provisional para <strong>{approveTarget?.email}</strong>. Compártela con el usuario por un canal seguro;
              se le pedirá que la cambie la próxima vez que acceda.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="approve-temp-password">Contraseña provisional (mín. 12 caracteres)</Label>
              <Input
                id="approve-temp-password"
                type="text"
                value={approveTempPassword}
                onChange={(e) => setApproveTempPassword(e.target.value)}
                placeholder="Provisional1234"
                autoComplete="new-password"
              />
              <Button type="button" variant="outline" size="sm" onClick={() => setApproveTempPassword(generateTemporaryPassword())}>
                Generar contraseña provisional
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setApproveTarget(null); setApproveTempPassword(""); }} disabled={approving}>
              Cancelar
            </Button>
            <Button onClick={handleApproveReset} disabled={approving} className="bg-[hsl(var(--brand-green))] hover:bg-[hsl(var(--brand-green))]/90 text-white">
              {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aprobar y establecer contraseña"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>

  );
};

export default AdminPanel;
